import { format, isValid, parse } from 'date-fns';

import { EU_LOCALE, NA_LOCALE } from '@shared/constants/region-locale';

export const formatRecurrenceRule = (rule?: string, endDateFormat = 'MM/dd/yy'): string => {
  if (!rule) {
    return '';
  }

  // One-time scheduled transaction (default rule)
  if (rule === 'FREQ=DAILY;COUNT=1') {
    return 'Once-off';
  }

  if (rule === 'FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR') {
    return 'Every weekday (Monday to Friday)';
  }

  const freqMap: Record<string, string> = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    YEARLY: 'Annually',
  };

  const freqPluralMap: Record<string, string> = {
    DAILY: 'day',
    WEEKLY: 'week',
    MONTHLY: 'month',
    YEARLY: 'year',
  };

  const dayMap: Record<string, string> = {
    SU: 'Sunday',
    MO: 'Monday',
    TU: 'Tuesday',
    WE: 'Wednesday',
    TH: 'Thursday',
    FR: 'Friday',
    SA: 'Saturday',
  };

  const monthMap: Record<string, string> = {
    '1': 'January',
    '2': 'February',
    '3': 'March',
    '4': 'April',
    '5': 'May',
    '6': 'June',
    '7': 'July',
    '8': 'August',
    '9': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
  };

  const getOrdinal = (number: number) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = number % 100;
    return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  const ruleChunks = rule.split(';').reduce((obj: Record<string, string>, chunk) => {
    const chunkParsed = chunk.split('=');
    return { ...obj, [chunkParsed[0]]: chunkParsed[1] };
  }, {});

  const freq = ruleChunks.FREQ;
  if (!freqMap[freq]) {
    // A rule without a frequency we can name cannot be described; an empty value hides the Recurrence row.
    return '';
  }

  const interval = ruleChunks.INTERVAL;
  const count = ruleChunks.COUNT;
  const until = ruleChunks.UNTIL;
  const untilDate = until ? parse(until.split('T')[0], 'yyyyMMdd', new Date()) : undefined;
  const untilParsed = untilDate && isValid(untilDate) ? format(untilDate, endDateFormat) : undefined;
  const byMonth = ruleChunks.BYMONTH;
  const byMonthDay = ruleChunks.BYMONTHDAY;
  const byDay = ruleChunks.BYDAY;
  const isWeekly = freq === 'WEEKLY';
  const isMonthly = freq === 'MONTHLY';
  const isYearly = freq === 'YEARLY';
  const byPos = parseInt(ruleChunks.BYSETPOS, 10);
  const weekLabels = ['first', 'second', 'third', 'fourth', 'fifth'];
  const weekLabel = byPos === -1 ? 'last' : weekLabels[byPos - 1];

  const freqLabel = interval === '1' || !interval ? freqMap[freq] : `Every ${interval} ${freqPluralMap[freq]}s`;
  const weekDays = (byDay ?? '')
    .split(',')
    .map((day) => dayMap[day])
    .filter(Boolean);
  const weekDayLabel = isWeekly && weekDays.length ? ` on ${weekDays.join(', ')}` : '';
  const monthDayLabel = isMonthly && byMonthDay ? ` on the ${getOrdinal(parseInt(byMonthDay, 10))}` : '';
  const yearDayLabel = isYearly && byMonthDay && monthMap[byMonth] ? ` on ${monthMap[byMonth]} ${byMonthDay}` : '';
  const monthWeekAndDayLabel = weekLabel && dayMap[byDay] ? ` on the ${weekLabel} ${dayMap[byDay]}` : '';
  const occurrencesCountLabel = count === '1' ? ', 1 occurrence' : `, ${count} occurrences`;
  const occurrencesLabel = count ? occurrencesCountLabel : '';
  const untilLabel = untilParsed ? `, until ${untilParsed}` : '';

  return `${freqLabel}${weekDayLabel}${monthDayLabel}${monthWeekAndDayLabel}${yearDayLabel}${occurrencesLabel}${untilLabel}`;
};

const OFFSET_SUFFIX = /(?:z|[+-]\d{2}:?\d{2})$/iu;

const scheduleDateTimeParts = (date: Date, timeZone: string, locale: string): Intl.DateTimeFormatPart[] =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(locale === EU_LOCALE ? { hour: '2-digit', hourCycle: 'h23' } : { hour: 'numeric', hour12: true }),
    minute: '2-digit',
  }).formatToParts(date);

/**
 * The backend sends `dateTimeString` as a wall-clock time in `timeZone` with no offset (a Java `LocalDateTime`).
 * Reading it as UTC and formatting it in UTC renders exactly the digits sent; parsing it in the browser's zone would
 * shift a time that falls in the browser's own DST gap. A value that does carry an offset is an instant, and is
 * converted into `timeZone` so it reads correctly under that label. Anything unreadable, including an instant in a zone
 * the browser does not know, is returned unchanged rather than shown wrong.
 */
export const formatScheduleDateTime = (dateTimeString?: string, timeZone?: string, requestedLocale?: string): string => {
  if (!dateTimeString) {
    return '';
  }

  const zone = timeZone || 'UTC';
  const locale = requestedLocale === EU_LOCALE ? EU_LOCALE : NA_LOCALE;
  try {
    const isInstant = OFFSET_SUFFIX.test(dateTimeString);
    const parts = isInstant
      ? scheduleDateTimeParts(new Date(dateTimeString), zone, locale)
      : scheduleDateTimeParts(new Date(`${dateTimeString}Z`), 'UTC', locale);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
    const time = `${part('hour')}:${part('minute')}`;
    return locale === EU_LOCALE
      ? `${part('day')} ${part('month')} ${part('year')} at ${time} (${zone})`
      : `${part('month')} ${part('day')}, ${part('year')} at ${time} ${part('dayPeriod')} (${zone})`;
  } catch {
    return dateTimeString;
  }
};
